/**
 * Render a single job detail view
 *
 * @package Interact_26
 */

import { getApplyOptions, openApplyModal } from './apply-modal';
import { type PinpointJob, type CareersConfig } from './types';
import { getMappedDepartment, escapeHtml, escapeAttr, sanitizeHtml } from './utils';

const BACK_ARROW_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M10.8284 12.0007L15.7782 16.9504L14.364 18.3646L8 12.0007L14.364 5.63672L15.7782 7.05093L10.8284 12.0007Z"/></svg>`;

/**
 * Render the full job detail into the container.
 */
export const renderJobDetail = (
  job: PinpointJob,
  container: HTMLElement,
  config: CareersConfig,
  onBackClick: () => void,
): void => {
  const department = getMappedDepartment(job.department, config);

  const metaItems = [
    { label: config.i18n.location, value: job.location },
    { label: config.i18n.employmentType, value: job.employment_type },
    { label: config.i18n.department, value: department },
    { label: config.i18n.salary, value: job.salary },
  ].filter((item) => item.value);

  const sections = [
    { title: config.i18n.description, content: job.description },
    { title: config.i18n.responsibilities, content: job.responsibilities },
    { title: config.i18n.skills, content: job.skills },
    { title: config.i18n.benefits, content: job.benefits },
  ].filter((section) => section.content);

  const metaHtml = metaItems
    .map(
      (item) => `
      <div class="careers-job-detail__meta-item">
        <span class="careers-job-detail__meta-label typography typo-body typo-small typo-font-body">${escapeHtml(item.label)}</span>
        <span class="careers-job-detail__meta-value typography typo-body typo-medium typo-font-body">${escapeHtml(item.value)}</span>
      </div>`,
    )
    .join('');

  const sectionsHtml = sections
    .map(
      (section) => `
      <div class="careers-job-detail__section">
        <h3 class="careers-job-detail__section-title typography typo-h3 typo-medium typo-font-heading">${escapeHtml(section.title)}</h3>
        <div class="careers-job-detail__section-content">${sanitizeHtml(section.content)}</div>
      </div>`,
    )
    .join('');

  container.innerHTML = `
    <div class="careers-job-detail__inner">
      <button type="button" class="careers-job-detail__back" data-action="back-to-jobs">
        ${BACK_ARROW_SVG}
        <span>${escapeHtml(config.i18n.backToJobs)}</span>
      </button>

      <h1 class="careers-job-detail__title typography typo-h2 typo-xlarge typo-font-heading">${escapeHtml(job.title)}</h1>

      ${metaItems.length > 0 ? `<div class="careers-job-detail__meta">${metaHtml}</div>` : ''}

      <div class="careers-job-detail__apply">
        <a href="${escapeAttr(job.url)}" target="_blank" rel="noopener noreferrer" data-action="apply" class="interact-button button--primary button--large">
          ${escapeHtml(config.i18n.applyNow)}
        </a>
      </div>

      ${sectionsHtml}
    </div>`;

  const backButton = container.querySelector('[data-action="back-to-jobs"]');
  if (backButton) {
    backButton.addEventListener('click', onBackClick);
  }

  const applyLink = container.querySelector<HTMLAnchorElement>('[data-action="apply"]');
  const applyOptions = getApplyOptions(job.url, config);

  if (applyLink && applyOptions) {
    applyLink.addEventListener('click', (event) => {
      event.preventDefault();
      openApplyModal(applyOptions, job.title, config, applyLink);
    });
  }
};
